import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuCircleAlert, LuPlus, LuRefreshCw, LuSparkles, LuTrash2, LuWandSparkles } from 'react-icons/lu';
import { API_BASE_URL } from '../../config.js';
import useAnalytics from '../../hooks/useAnalytics.jsx';
import { Card, CardHeader } from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import EmptyState from '../ui/EmptyState';
import ConfirmModal from '../ui/ConfirmModal';
import ProgressBar from '../ui/ProgressBar';
import Skeleton from '../ui/Skeleton';
import { SpinnerRing } from '../ui/Spinner';
import { AnalysisDisplay, AnalysisOutline } from './AnalysisDisplay';
import { AnalysisEditor } from './AnalysisEditor';
import { AnalysisTabs } from './AnalysisTabs';
import { ModalAnalysis } from './AnalysisModals';
import {
  analysisError, analysisPrompt, analysisTitle, isCustomAnalysis, parseAnalysisData, sortAnalysesByConfig,
} from './analysisContent.js';
import { analyzeProgressKey } from './reportStatus.js';
import { useReportAnalyses } from '../../hooks/useReportAnalyses.js';

/** Waiting / failed / empty states, driven by the analyze step of the report. */
const AnalysesState = ({ report, isAdminUser, onRegenerateAll }) => {
  const analyze = report?.steps?.analyze;
  if (analyze?.status === 'started') {
    const done = analyze.analyzed_count || 0;
    const total = analyze.total_count || 0;
    return (
      <div className="flex flex-col items-center px-6 py-14 text-center">
        <SpinnerRing size="md" label="Analyse en cours" />
        <p className="mt-4 text-sm font-medium text-gray-900 dark:text-white">Analyse des messages en cours…</p>
        {total > 0 && (
          <div className="mt-3 w-full max-w-xs">
            <ProgressBar value={(done / total) * 100} label="Progression de l'analyse" />
            <p className="mt-1.5 text-xs tabular-nums text-gray-500 dark:text-gray-400">{done} / {total} analyses</p>
          </div>
        )}
      </div>
    );
  }
  if (analyze?.status === 'failed') {
    return (
      <EmptyState
        icon={LuCircleAlert}
        tone="red"
        title="L'analyse des données a échoué"
        description={isAdminUser ? 'Relancez la génération : les messages de la semaine sont toujours disponibles.' : 'Les analyses de cette semaine ne sont pas disponibles pour le moment.'}
        action={isAdminUser && <Button icon={LuRefreshCw} onClick={onRegenerateAll}>Régénérer les analyses</Button>}
      />
    );
  }
  if (analyze?.status === 'completed') {
    return <EmptyState icon={LuSparkles} tone="gray" title="Aucune analyse disponible" description="Aucune analyse n'a été produite pour cette semaine." />;
  }
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <SpinnerRing size="md" label="En attente de l'analyse" />
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">En attente de l'analyse des données…</p>
    </div>
  );
};

export const AnalysesSection = ({
  report,
  fetchWithAuth,
  clientSlug,
  weekId,
  client,
  isAdminUser,
  isPublicView = false,
  analyses = null,
  currentUser,
  activeType: controlledType,
  onActiveTypeChange,
  analysesStore = null,
}) => {
  // The report page owns the analyses (its sidebar counts them); the public view passes a list.
  const ownStore = useReportAnalyses({
    enabled: !analysesStore,
    fetchWithAuth,
    clientSlug,
    weekId,
    initial: analyses,
    refreshKey: analyzeProgressKey(report),
  });
  const {
    analyses: localAnalyses, setAnalyses: setLocalAnalyses, loadState, setLoadState, reload: fetchAnalyses,
  } = analysesStore || ownStore;
  // Controlled by the report page (survives tab switches); local in the public view.
  const [ownType, setOwnType] = useState(null);
  const controlled = onActiveTypeChange !== undefined;
  const activeType = controlled ? controlledType : ownType;
  const setActiveType = controlled ? onActiveTypeChange : setOwnType;
  const [regenerating, setRegenerating] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmOne, setConfirmOne] = useState(null);
  const [regeneratingType, setRegeneratingType] = useState(null);
  const [actionError, setActionError] = useState(null);
  const modalRef = useRef(null);

  const { track } = useAnalytics({ currentUser, report });

  const getApiUrl = (endpoint) => `${API_BASE_URL}/api/weekly/client/${clientSlug}/week/${weekId}${endpoint}`;
  const defaultHeaders = { 'Content-Type': 'application/json' };

  const handleApiResponse = async (response, errorMessage) => {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorMessage);
    }
    return response.json();
  };

  // Default tab: first analysis in the configured order; recover if the active one disappears.
  useEffect(() => {
    if (localAnalyses.length === 0) return;
    if (activeType && localAnalyses.some((a) => a.type === activeType)) return;
    setActiveType(sortAnalysesByConfig(localAnalyses, client?.weekly_config)[0].type);
  }, [localAnalyses, client?.weekly_config, activeType, setActiveType]);

  // Controlled: the report page tracks the selection (once, not at every tab switch).
  useEffect(() => {
    if (!controlled && activeType) track('view_weekly_analysis', { analysis: activeType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const handleAnalysisSave = useCallback((updatedAnalysis, analysisType) => {
    setLocalAnalyses((prev) => prev.map((a) => (a.type === analysisType ? updatedAnalysis : a)));
  }, [setLocalAnalyses]);

  const createAnalysis = async ({ title, prompt }) => {
    const response = await fetchWithAuth(getApiUrl('/analyses/personalized'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ prompt, title }),
    });
    const saved = await handleApiResponse(response, 'La création de l\'analyse a échoué');
    await fetchAnalyses();
    if (saved?.type) setActiveType(saved.type);
    return saved;
  };

  const handleRegenerateAnalyses = async () => {
    try {
      track('regenerate_weekly_analyses');
      setRegenerating(true);
      setActionError(null);
      const response = await fetchWithAuth(getApiUrl('/regenerate-analyses'), { method: 'POST', headers: defaultHeaders });
      await handleApiResponse(response, 'Error regenerating analyses');
      // The analyzer restarts server-side: reload to pick the new analyses up from scratch.
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('Error regenerating analyses:', err);
      setActionError('La régénération des analyses a échoué.');
      setRegenerating(false);
      setConfirmAll(false);
    }
  };

  const handleRegenerateAnalysis = async (type) => {
    try {
      track('regenerate_weekly_analysis', { regenerated_analysis: type });
      setRegeneratingType(type);
      setActionError(null);
      const response = await fetchWithAuth(getApiUrl(`/regenerate-analyses?type=${type}`), { method: 'POST' });
      const fresh = await handleApiResponse(response, 'Failed to regenerate analysis');
      if (Array.isArray(fresh)) setLocalAnalyses(fresh);
    } catch (error) {
      console.error('Error regenerating analysis:', error);
      setActionError('La régénération de l\'analyse a échoué.');
    } finally {
      setRegeneratingType(null);
      setConfirmOne(null);
    }
  };

  const handleRegenerateCustomAnalysis = async (type, newPrompt) => {
    track('regenerate_weekly_analysis', { regenerated_analysis: type });
    const target = localAnalyses.find((a) => a.type === type);
    if (!target) throw new Error('Analysis not found');
    setRegeneratingType(type);
    try {
      const response = await fetchWithAuth(getApiUrl(`/analyses/${target.id}/regenerate`), {
        method: 'PUT',
        headers: defaultHeaders,
        body: JSON.stringify({ prompt: newPrompt }),
      });
      const result = await handleApiResponse(response, 'La régénération de l\'analyse a échoué');
      const updated = {
        ...result.analysis,
        custom_analysis: { prompt: newPrompt, analyse: parseAnalysisData(result.analysis.ia_analysis).analyse || {} },
      };
      setLocalAnalyses((prev) => prev.map((a) => (a.id === target.id ? updated : a)));
    } finally {
      setRegeneratingType(null);
    }
  };

  const handleDeleteCustomAnalysis = async (analysisId) => {
    const response = await fetchWithAuth(getApiUrl(`/analyses/${analysisId}`), { method: 'DELETE', headers: defaultHeaders });
    const result = await handleApiResponse(response, 'La suppression de l\'analyse a échoué');
    await fetchAnalyses();
    return result;
  };

  const activeAnalysis = localAnalyses.find((a) => a.type === activeType);
  const activeIsCustom = isCustomAnalysis(activeAnalysis);
  const activeFailure = analysisError(activeAnalysis);
  const regeneratingActive = regeneratingType && regeneratingType === activeAnalysis?.type;

  const requestRegenerate = () => {
    if (!activeAnalysis) return;
    if (activeIsCustom) modalRef.current?.openRegenerate(activeAnalysis);
    else setConfirmOne(activeAnalysis);
  };

  return (
    <>
      <Card>
        <CardHeader
          icon={LuSparkles}
          title="Analyses"
          subtitle="Synthèse IA des retours terrain de la semaine"
          badge={localAnalyses.length > 0 && <Badge tone="violet" size="sm">{localAnalyses.length}</Badge>}
          actions={isAdminUser && !isPublicView && (
            <>
              <Button size="sm" icon={LuPlus} onClick={() => modalRef.current?.openCreate()}>Analyse personnalisée</Button>
              <Button size="sm" variant="ghost" icon={LuRefreshCw} onClick={() => setConfirmAll(true)} loading={regenerating}>Tout régénérer</Button>
            </>
          )}
        />

        {actionError && <div className="px-4 pt-4 sm:px-5"><Alert tone="danger">{actionError}</Alert></div>}

        {loadState === 'loading' ? (
          <div className="space-y-3 p-4 sm:p-5" aria-busy="true">
            <Skeleton className="h-10 w-80" />
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : loadState === 'error' && localAnalyses.length === 0 ? (
          <EmptyState
            icon={LuCircleAlert}
            tone="red"
            title="Impossible de charger les analyses"
            description="Vérifiez votre connexion puis réessayez."
            action={<Button icon={LuRefreshCw} onClick={() => { setLoadState('loading'); fetchAnalyses(); }}>Réessayer</Button>}
          />
        ) : localAnalyses.length === 0 ? (
          <AnalysesState report={report} isAdminUser={isAdminUser && !isPublicView} onRegenerateAll={() => setConfirmAll(true)} />
        ) : (
          <div className="space-y-4 p-4 sm:p-5">
            <AnalysisTabs analyses={localAnalyses} activeType={activeType} onActiveTypeChange={setActiveType} client={client} />

            {activeAnalysis && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">{analysisTitle(activeAnalysis, client?.weekly_config)}</h3>
                    {activeIsCustom && <Badge tone="violet" size="sm" icon={LuWandSparkles}>Personnalisée</Badge>}
                  </div>
                  {activeIsCustom && isAdminUser && analysisPrompt(activeAnalysis) && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-gray-500 dark:text-gray-400" title={analysisPrompt(activeAnalysis)}>
                      Prompt : {analysisPrompt(activeAnalysis)}
                    </p>
                  )}
                </div>
                {isAdminUser && !isPublicView && (
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <Button size="sm" variant="ghost" icon={LuRefreshCw} onClick={requestRegenerate} loading={regeneratingActive}>
                      Régénérer
                    </Button>
                    {activeIsCustom && (
                      <Button size="sm" variant="danger-ghost" icon={LuTrash2} onClick={() => modalRef.current?.openDelete(activeAnalysis)}>
                        Supprimer
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeAnalysis ? (
              activeFailure ? (
                <Alert tone="danger" title="Cette analyse a échoué">{isAdminUser ? activeFailure : 'Elle n\'est pas disponible pour le moment.'}</Alert>
              ) : (
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_14rem]">
                  <div className="min-w-0">
                    {isAdminUser && !isPublicView ? (
                      <AnalysisEditor
                        key={activeAnalysis.id ?? activeAnalysis.type}
                        analysis={activeAnalysis}
                        onSave={(updated) => handleAnalysisSave(updated, activeAnalysis.type)}
                        fetchWithAuth={fetchWithAuth}
                        clientSlug={clientSlug}
                        weekId={weekId}
                      />
                    ) : (
                      <AnalysisDisplay analysis={activeAnalysis} />
                    )}
                  </div>
                  <aside className="hidden xl:block">
                    <AnalysisOutline analysis={activeAnalysis} />
                  </aside>
                </div>
              )
            ) : (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Aucune analyse sélectionnée</p>
            )}
          </div>
        )}
      </Card>

      {isAdminUser && !isPublicView && (
        <>
          <ConfirmModal
            isOpen={confirmAll}
            onClose={() => !regenerating && setConfirmAll(false)}
            onConfirm={handleRegenerateAnalyses}
            loading={regenerating}
            icon={LuRefreshCw}
            title="Régénérer toutes les analyses ?"
            message="Toutes les analyses de ce rapport, modifications manuelles comprises, seront supprimées puis recalculées à partir des messages de la semaine."
            confirmLabel="Tout régénérer"
          />
          <ConfirmModal
            isOpen={confirmOne !== null}
            onClose={() => !regeneratingType && setConfirmOne(null)}
            onConfirm={() => handleRegenerateAnalysis(confirmOne.type)}
            loading={!!regeneratingType}
            icon={LuRefreshCw}
            title="Régénérer cette analyse ?"
            message={<>« {analysisTitle(confirmOne, client?.weekly_config)} » sera recalculée. Les modifications manuelles de cette analyse seront perdues.</>}
            confirmLabel="Régénérer"
          />
          <ModalAnalysis
            ref={modalRef}
            onCreateAnalysis={createAnalysis}
            onDeleteAnalysis={handleDeleteCustomAnalysis}
            onRegenerateCustomAnalysis={handleRegenerateCustomAnalysis}
            extractOriginalPrompt={analysisPrompt}
          />
        </>
      )}
    </>
  );
};

export default AnalysesSection;
